/**
 * SubgraphEntityFormPage — Generic, Data-Driven Entity Form (DRR-055)
 *
 * ONE page that renders ANY entity type from ANY subgraph.
 * ALL configuration (safety level, compliance, icon, approval requirements)
 * comes from SubgraphMetadata — ZERO hardcoded domain logic.
 *
 * Route pattern: /admin/:subgraph/entities/:entityType/create
 *                /admin/:subgraph/entities/:entityType/:id
 *                /admin/:subgraph/entities/:entityType/:id/edit
 *                /admin/:subgraph/entities/:entityType/:id/approve
 *
 * FERP Principles:
 * - Dumb Frontend: Form schema comes 100% from backend
 * - Data-driven: Safety badges, compliance tags, approval mode from SubgraphMetadata
 * - Repository pattern: All mutations through GraphQL
 * - Zero domain-specific pages: ONE component serves all entity types
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Alert,
  Loader,
  Center,
  ThemeIcon,
  Paper,
  Breadcrumbs,
  Anchor,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconArrowLeft,
  IconCheck,
  IconX,
  IconHeartbeat,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EntityFormGenerator } from '@ferp/workflow-forms';
import type { UIContext, UIMode } from '@ferp/workflow-forms';
import { useTenantSubgraphConfig } from '@/hooks/useTenantSubgraphConfig';
import { getIcon } from '@/utils/iconMap';
import {
  useCreateEntityMutation,
  useUpdateEntityQueryLazyQuery,
  useCreateEntityApprovalMutation,
} from '@/generated/graphql';

// GraphQL operations defined in:
// - mutations.graphql: CreateEntity, CreateEntityApproval
// - queries-part3.graphql: UpdateEntityQuery (updateEntity is on Query root)

// =============================================================================
// COMPONENT
// =============================================================================

interface SubgraphEntityFormPageProps {
  defaultMode?: UIMode;
}

export function SubgraphEntityFormPage({ defaultMode = 'VIEW' }: SubgraphEntityFormPageProps): React.ReactElement {
  const navigate = useNavigate();
  const { subgraph, entityType, id } = useParams<{ subgraph: string; entityType: string; id: string }>();
  const [searchParams] = useSearchParams();
  const tenantId = useTenantId() ?? '';

  const [createEntity] = useCreateEntityMutation();
  const [updateEntity] = useUpdateEntityQueryLazyQuery();
  const [createApproval] = useCreateEntityApprovalMutation();

  // Resolve mode from URL structure or query param
  const mode: UIMode = useMemo(() => {
    const urlMode = searchParams.get('mode')?.toUpperCase();
    if (urlMode && ['CREATE', 'EDIT', 'VIEW', 'APPROVE', 'FILTER'].includes(urlMode)) {
      return urlMode as UIMode;
    }
    return defaultMode;
  }, [searchParams, defaultMode]);

  // DRR-162: RuleSet ID for service-line-specific form resolution.
  // Passed via ?rulesetId=rs-4-1-004 from constellation context or entity browser.
  const rulesetId = searchParams.get('rulesetId') ?? undefined;

  // Load entity type metadata from SubgraphMetadata (data-driven)
  const { getSubgraphConfig, loading: configLoading } = useTenantSubgraphConfig(tenantId);

  const subgraphConfig = useMemo(() => {
    return subgraph ? getSubgraphConfig(subgraph) : null;
  }, [getSubgraphConfig, subgraph]);

  const entityMeta = useMemo(() => {
    if (!subgraphConfig || !entityType) return null;
    return subgraphConfig.entityTypes.find(
      (et) => et.graphqlType.toLowerCase() === entityType.toLowerCase() || et.name.toLowerCase() === entityType.toLowerCase()
    ) ?? null;
  }, [subgraphConfig, entityType]);

  // Derive safety level + compliance from metadata
  const safetyLevel = entityMeta?.isLifeCritical ? 'CRITICAL' : 'ELEVATED';
  const complianceFrameworks = entityMeta?.complianceFrameworks ?? subgraphConfig?.complianceFrameworks ?? [];
  const isLifeCritical = entityMeta?.isLifeCritical ?? false;

  const context: UIContext = useMemo(() => ({
    mode,
    tenantId,
    safetyLevel,
  }), [mode, tenantId, safetyLevel]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const backPath = `/admin/${subgraph}`;

  const handleSubmit = useCallback(async (values: Record<string, unknown>) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (mode === 'CREATE') {
        await createEntity({
          variables: {
            input: {
              name: (values.name as string) || entityMeta?.name || entityType || 'Entity',
              entityType: entityMeta?.graphqlType || entityType,
              metadata: {
                safetyLevel,
                complianceFramework: complianceFrameworks[0] ?? null,
                subgraph,
              },
              configuration: values,
            },
          },
        });
      } else if (mode === 'EDIT' && id) {
        await updateEntity({ variables: { input: { id, configuration: values } } });
      } else if (mode === 'APPROVE' && id) {
        await createApproval({
          variables: {
            tenantId,
            input: {
              approvalName: `${entityMeta?.graphqlType ?? entityType}-${id}`,
              approvalType: 'SEQUENTIAL',
              approvalStrategy: isLifeCritical ? 'UNANIMOUS' : 'MAJORITY',
              approverId: tenantId,
              approverRole: 'reviewer',
            },
          },
        });
      }
      setSubmitSuccess(true);
      setTimeout(() => navigate(backPath), 1500);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : `Failed to save ${entityMeta?.name ?? entityType}`);
    } finally {
      setSubmitting(false);
    }
  }, [mode, id, tenantId, entityType, entityMeta, subgraph, safetyLevel, complianceFrameworks, isLifeCritical, navigate, backPath, createEntity, updateEntity, createApproval]);

  const handleReject = useCallback(async () => {
    if (!id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await updateEntity({ variables: { input: { id, configuration: { status: 'REJECTED' } } } });
      setSubmitSuccess(true);
      setTimeout(() => navigate(backPath), 1500);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : `Failed to reject ${entityMeta?.name ?? entityType}`);
    } finally {
      setSubmitting(false);
    }
  }, [id, navigate, backPath, updateEntity, entityMeta, entityType]);

  const displayName = entityMeta?.name ?? entityType ?? 'Entity';
  const pluralName = entityMeta?.pluralName ?? `${displayName}s`;

  const getPageTitle = () => {
    switch (mode) {
      case 'CREATE': return `Create ${displayName}`;
      case 'EDIT': return `Edit ${displayName}`;
      case 'VIEW': return `View ${displayName}`;
      case 'APPROVE': return `Approve ${displayName}`;
      default: return displayName;
    }
  };

  const getModeBadgeColor = () => {
    switch (mode) {
      case 'CREATE': return 'green';
      case 'EDIT': return 'blue';
      case 'VIEW': return 'gray';
      case 'APPROVE': return 'orange';
      default: return 'gray';
    }
  };

  // Loading metadata
  if (configLoading || !subgraph || !entityType) {
    return (
      <Container size="xl" py="xl">
        <Center h={400}>
          <Stack ta="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading entity configuration...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Entity type not found in metadata
  if (!entityMeta) {
    return (
      <Container size="xl" py="xl">
        <Alert color="yellow" title="Entity Type Not Found">
          No metadata found for entity type "{entityType}" in subgraph "{subgraph}".
          Check SubgraphMetadata configuration.
        </Alert>
      </Container>
    );
  }

  // Resolve icon from metadata
  const EntityIcon = getIcon(entityMeta.icon);

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Breadcrumbs — fully data-driven */}
        <Breadcrumbs>
          <Anchor href={backPath} onClick={(e) => { e.preventDefault(); navigate(backPath); }}>
            {subgraphConfig?.displayName ?? subgraph}
          </Anchor>
          <Anchor href={backPath} onClick={(e) => { e.preventDefault(); navigate(backPath); }}>
            {pluralName}
          </Anchor>
          <Text>{getPageTitle()}</Text>
        </Breadcrumbs>

        {/* Header — safety/compliance badges from metadata */}
        <Group justify="space-between" ta="left">
          <div>
            <Group gap="sm">
              <ThemeIcon size="lg" variant="light" color={subgraphConfig?.color ?? 'blue'}>
                <EntityIcon size={20} />
              </ThemeIcon>
              <div>
                <Title order={2}>{getPageTitle()}</Title>
                <Text c="dimmed" size="sm">{entityMeta.description}</Text>
              </div>
            </Group>
          </div>
          <Group>
            <Badge color={getModeBadgeColor()} variant="filled">{mode}</Badge>
            {isLifeCritical && (
              <Badge color="red" variant="light" leftSection={<IconHeartbeat size={12} />}>LIFE-CRITICAL</Badge>
            )}
            {complianceFrameworks.map((fw) => (
              <Badge key={fw} color="teal" variant="light" leftSection={<IconShieldCheck size={12} />}>{fw}</Badge>
            ))}
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(backPath)}>
              Back to {subgraphConfig?.displayName ?? subgraph}
            </Button>
          </Group>
        </Group>

        {/* Life-critical warning — shown only when metadata says so */}
        {isLifeCritical && (
          <Alert icon={<IconHeartbeat size={16} />} title={`Life-Critical ${displayName}`} color="red" variant="light">
            This entity is classified as LIFE-CRITICAL. All changes are subject to
            compliance auditing and may require dual approval.
          </Alert>
        )}

        {/* Approval mode — driven by metadata, not hardcoded per entity */}
        {mode === 'APPROVE' && (
          <Alert icon={<IconShieldCheck size={16} />} title="Approval Required" color="orange" variant="light">
            This {displayName.toLowerCase()} requires review and approval before activation.
          </Alert>
        )}

        {submitSuccess && (
          <Alert icon={<IconCheck size={16} />} title="Success" color="green" variant="filled">
            {displayName} {mode === 'CREATE' ? 'created' : mode === 'APPROVE' ? 'approved' : 'saved'} successfully. Redirecting...
          </Alert>
        )}

        {submitError && (
          <Alert icon={<IconX size={16} />} title="Error" color="red" variant="light" withCloseButton onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {/* Form — EntityFormGenerator is already data-driven via subgraph + entityType */}
        <Paper withBorder p="xl">
          <EntityFormGenerator
            subgraph={subgraph}
            entityType={entityMeta.graphqlType}
            rulesetId={rulesetId}
            context={context}
            onSubmit={handleSubmit}
            disabled={submitting || submitSuccess || mode === 'VIEW'}
            loading={submitting}
          />
        </Paper>

        {/* Approval actions — shown for any entity in APPROVE mode */}
        {mode === 'APPROVE' && !submitSuccess && (
          <Paper withBorder p="md">
            <Group justify="flex-end">
              <Button variant="outline" color="red" leftSection={<IconX size={16} />} onClick={handleReject} disabled={submitting}>
                Reject
              </Button>
              <Button variant="filled" color="green" leftSection={<IconCheck size={16} />} onClick={() => handleSubmit({})} loading={submitting}>
                Approve
              </Button>
            </Group>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

// =============================================================================
// ROUTE-SPECIFIC WRAPPERS (mode presets)
// =============================================================================

export function SubgraphEntityCreatePage(): React.ReactElement {
  return <SubgraphEntityFormPage defaultMode="CREATE" />;
}

export function SubgraphEntityEditPage(): React.ReactElement {
  return <SubgraphEntityFormPage defaultMode="EDIT" />;
}

export function SubgraphEntityViewPage(): React.ReactElement {
  return <SubgraphEntityFormPage defaultMode="VIEW" />;
}

export function SubgraphEntityApprovePage(): React.ReactElement {
  return <SubgraphEntityFormPage defaultMode="APPROVE" />;
}

export default SubgraphEntityFormPage;
